const REQUIRED_PICKER_SELECTOR = 'variant-picker[data-require-size-selection="true"]';
const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 749px)');

const LABELS = Object.freeze({
  selectSize: 'Select a size',
  addToCart: 'Add to cart',
  error: 'Please select a size',
});

/**
 * @type {WeakMap<HTMLElement, {
 *   rememberedSize: object | null,
 *   requestGeneration: number,
 *   mobileStickyObserver: IntersectionObserver | null,
 *   mobileStickyTarget: Element | null,
 *   mobileStickyMainActionVisible: boolean,
 *   mobileMediaHandler: (() => void) | null
 * }>}
 */
const pickerStates = new WeakMap();

function nextFrame(callback) {
  requestAnimationFrame(callback);
}

function findProductElement(root, selector, productId) {
  const candidates = Array.from(root.querySelectorAll(selector));

  if (!productId) return candidates[0] || null;

  return (
    candidates.find(
      (candidate) =>
        candidate instanceof HTMLElement && candidate.dataset.productId === productId
    ) || candidates[0] || null
  );
}

function getContext(picker) {
  const section = picker.closest('.shopify-section') || picker.closest('section') || document;
  const productId = picker.dataset.productId || '';
  const productForm = findProductElement(
    section,
    'product-form-component',
    productId
  );
  const sticky = findProductElement(section, 'sticky-add-to-cart', productId);
  const layout =
    picker.closest('.product-information--reference-layout') ||
    picker.closest('[class*="product-information"]') ||
    (section instanceof HTMLElement ? section : null);

  return { section, productForm, sticky, layout };
}

function getSizeContainer(picker) {
  const container = picker.querySelector('[data-size-option]');
  return container instanceof HTMLElement ? container : null;
}

function getSelectedSize(sizeContainer) {
  if (!(sizeContainer instanceof HTMLElement)) return null;

  const radio = sizeContainer.querySelector('input[type="radio"]:checked');
  if (radio instanceof HTMLInputElement) {
    return {
      kind: 'radio',
      control: radio,
      option: radio,
      optionValueId: radio.dataset.optionValueId || '',
      value: radio.value || '',
      label: radio.getAttribute('aria-label') || radio.value || '',
    };
  }

  const select = sizeContainer.querySelector('select');
  if (!(select instanceof HTMLSelectElement) || select.selectedIndex < 0) return null;

  const option = select.options[select.selectedIndex];
  if (!(option instanceof HTMLOptionElement) || !option.value) return null;

  return {
    kind: 'select',
    control: select,
    option,
    optionValueId: option.dataset.optionValueId || '',
    value: option.value || '',
    label: option.textContent?.replace(/\s+-\s+.*$/, '').trim() || option.value || '',
  };
}

function getSizeMemory(selection) {
  if (!selection) return null;

  return {
    kind: selection.kind,
    optionValueId: selection.optionValueId,
    value: selection.value,
    label: selection.label,
  };
}

function clearSizeSelection(sizeContainer) {
  if (!(sizeContainer instanceof HTMLElement)) return;

  sizeContainer.style.removeProperty('--pill-width-current');
  sizeContainer.style.removeProperty('--pill-width-previous');

  sizeContainer.querySelectorAll('input[type="radio"]').forEach((radio) => {
    if (!(radio instanceof HTMLInputElement)) return;

    radio.checked = false;
    radio.removeAttribute('checked');
    radio.dataset.currentChecked = 'false';
    radio.dataset.previousChecked = 'false';
  });

  sizeContainer.querySelectorAll('select').forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    Array.from(select.options).forEach((option) => {
      option.selected = false;
      option.removeAttribute('selected');
    });
    select.selectedIndex = -1;
  });
}

function findRememberedSize(sizeContainer, memory) {
  if (!(sizeContainer instanceof HTMLElement) || !memory) return null;

  const radios = Array.from(
    sizeContainer.querySelectorAll('input[type="radio"]')
  ).filter((radio) => radio instanceof HTMLInputElement);

  let radioMatch = null;
  if (memory.optionValueId) {
    radioMatch = radios.find(
      (radio) => radio.dataset.optionValueId === memory.optionValueId
    );
  }
  if (!radioMatch && memory.value) {
    radioMatch = radios.find((radio) => radio.value === memory.value);
  }
  if (!radioMatch && memory.label) {
    radioMatch = radios.find(
      (radio) => radio.getAttribute('aria-label') === memory.label
    );
  }

  if (radioMatch instanceof HTMLInputElement) {
    return { kind: 'radio', control: radioMatch, option: radioMatch };
  }

  const select = sizeContainer.querySelector('select');
  if (!(select instanceof HTMLSelectElement)) return null;

  const options = Array.from(select.options);
  let optionMatch = null;
  if (memory.optionValueId) {
    optionMatch = options.find(
      (option) => option.dataset.optionValueId === memory.optionValueId
    );
  }
  if (!optionMatch && memory.value) {
    optionMatch = options.find((option) => option.value === memory.value);
  }

  return optionMatch instanceof HTMLOptionElement
    ? { kind: 'select', control: select, option: optionMatch }
    : null;
}

function rememberedSizeIsSelected(match) {
  if (!match) return false;

  if (match.kind === 'radio') {
    return match.control instanceof HTMLInputElement && match.control.checked;
  }

  return (
    match.control instanceof HTMLSelectElement &&
    match.option instanceof HTMLOptionElement &&
    match.control.selectedOptions[0] === match.option
  );
}

function markSizeSelected(sizeContainer, match) {
  if (!(sizeContainer instanceof HTMLElement) || !match) return;

  sizeContainer.style.removeProperty('--pill-width-current');
  sizeContainer.style.removeProperty('--pill-width-previous');

  if (match.kind === 'radio' && match.control instanceof HTMLInputElement) {
    sizeContainer.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (!(radio instanceof HTMLInputElement)) return;

      const isSelected = radio === match.control;
      radio.checked = isSelected;

      if (isSelected) {
        radio.setAttribute('checked', '');
        radio.dataset.currentChecked = 'true';
        radio.dataset.previousChecked = 'false';
        sizeContainer.style.setProperty(
          '--pill-width-current',
          `${radio.parentElement?.offsetWidth || 0}px`
        );
      } else {
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }
    });
    return;
  }

  if (
    match.kind === 'select' &&
    match.control instanceof HTMLSelectElement &&
    match.option instanceof HTMLOptionElement
  ) {
    Array.from(match.control.options).forEach((option) => {
      const isSelected = option === match.option;
      option.selected = isSelected;
      option.toggleAttribute('selected', isSelected);
    });
  }
}

function getSizeError(picker) {
  const error = picker.querySelector('[data-size-selection-error]');
  return error instanceof HTMLElement ? error : null;
}

function setControlsInvalid(sizeContainer, invalid) {
  if (!(sizeContainer instanceof HTMLElement)) return;

  sizeContainer.querySelectorAll('input, select').forEach((control) => {
    if (!(control instanceof HTMLElement)) return;
    if (invalid) {
      control.setAttribute('aria-invalid', 'true');
    } else {
      control.removeAttribute('aria-invalid');
    }
  });
}

function showSizeError(picker) {
  const error = getSizeError(picker);
  const sizeContainer = getSizeContainer(picker);

  if (error) {
    const text = error.querySelector('span');
    if (text) text.textContent = LABELS.error;
    error.hidden = false;
  }

  picker.dataset.sizeValidationAttempted = 'true';
  setControlsInvalid(sizeContainer, true);
}

function hideSizeError(picker) {
  const error = getSizeError(picker);
  const sizeContainer = getSizeContainer(picker);

  if (error) error.hidden = true;
  picker.dataset.sizeValidationAttempted = 'false';
  setControlsInvalid(sizeContainer, false);
}

function getButtonNativeText(button) {
  const clone = button.cloneNode(true);
  if (!(clone instanceof HTMLElement)) return '';

  clone
    .querySelectorAll(
      '.add-to-cart__added, .quantity-display, [ref="quantityDisplay"], svg'
    )
    .forEach((element) => element.remove());

  return clone.textContent?.replace(/\s+/g, ' ').trim() || '';
}

function getOrCreateButtonLabel(button) {
  const existing = button.querySelector('[data-required-size-main-label]');
  if (existing instanceof HTMLElement) return existing;

  const currentNativeText = getButtonNativeText(button);
  if (
    currentNativeText &&
    currentNativeText !== LABELS.selectSize &&
    currentNativeText !== LABELS.addToCart
  ) {
    button.dataset.requiredSizeNativeLabel = currentNativeText;
  }

  const wrappedText = button.querySelector(
    '.add-to-cart-text__content > span:first-child'
  );
  if (wrappedText instanceof HTMLElement) {
    wrappedText.dataset.requiredSizeMainLabel = '';
    return wrappedText;
  }

  const directTextNode = Array.from(button.childNodes).find(
    (node) => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim())
  );
  const label = document.createElement('span');
  label.dataset.requiredSizeMainLabel = '';

  if (directTextNode) {
    directTextNode.replaceWith(label);
  } else {
    button.insertBefore(label, button.querySelector('.add-to-cart__added'));
  }

  return label;
}

function setMainButtonState(productForm, hasSelection, errorId) {
  if (!(productForm instanceof HTMLElement)) return;

  const currentSubmitAction = productForm.getAttribute('on:submit');
  if (currentSubmitAction) {
    productForm.dataset.requiredSizeSubmitAction = currentSubmitAction;
  }

  if (hasSelection) {
    productForm.setAttribute(
      'on:submit',
      productForm.dataset.requiredSizeSubmitAction || '/handleSubmit'
    );
  } else {
    productForm.removeAttribute('on:submit');
  }

  const buttons = productForm.querySelectorAll('[ref="addToCartButton"]');
  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    const currentClickAction = button.getAttribute('on:click');
    if (currentClickAction) {
      button.dataset.requiredSizeClickAction = currentClickAction;
    }
    if (!button.dataset.requiredSizeNativeType) {
      button.dataset.requiredSizeNativeType = button.getAttribute('type') || 'submit';
    }

    if (hasSelection) {
      button.setAttribute('type', button.dataset.requiredSizeNativeType);
      if (button.dataset.requiredSizeClickAction) {
        button.setAttribute('on:click', button.dataset.requiredSizeClickAction);
      }
    } else {
      button.setAttribute('type', 'button');
      button.removeAttribute('on:click');
    }

    const nativeText = getButtonNativeText(button);
    if (
      nativeText &&
      nativeText !== LABELS.selectSize &&
      nativeText !== LABELS.addToCart
    ) {
      button.dataset.requiredSizeNativeLabel = nativeText;
    }

    const label = getOrCreateButtonLabel(button);
    let nextLabel = button.dataset.requiredSizeNativeLabel || nativeText;

    if (!button.disabled) {
      // Main PDP CTA always reads Add to cart. Size validation still blocks
      // submission and shows the existing "Please select a size" message.
      nextLabel = LABELS.addToCart;
    }

    if (nextLabel) {
      label.textContent = nextLabel;
      button.setAttribute('aria-label', nextLabel);
    }

    button.dataset.requiredSizeButton = 'true';
    if (errorId) button.setAttribute('aria-describedby', errorId);
  });

  // Server markup starts inert to close the pre-hydration submit window.
  productForm.removeAttribute('inert');
}

function setAcceleratedCheckoutState(productForm, hasSelection) {
  if (!(productForm instanceof HTMLElement)) return;

  const checkout = productForm.querySelector('.accelerated-checkout-block');
  const mainButton = productForm.querySelector('[ref="addToCartButton"]');
  if (!(checkout instanceof HTMLElement)) return;

  const variantAvailable =
    mainButton instanceof HTMLButtonElement && !mainButton.disabled;
  const enabled = hasSelection && variantAvailable;

  let blocker = checkout.querySelector('[data-required-size-checkout-trigger]');
  if (!(blocker instanceof HTMLButtonElement)) {
    blocker = document.createElement('button');
    blocker.type = 'button';
    blocker.className = 'required-size-checkout-trigger';
    blocker.dataset.requiredSizeCheckoutTrigger = 'true';
    blocker.setAttribute('aria-label', LABELS.error);
    checkout.appendChild(blocker);
  }

  // Keep Buy It Now visible. Before a size is selected its click is caught by
  // the same validation controller instead of hiding the accelerated checkout.
  if (variantAvailable) {
    checkout.removeAttribute('hidden');
    checkout.removeAttribute('inert');
  }
  blocker.hidden = enabled || !variantAvailable;
  checkout.dataset.requiredSizeBlocked = enabled ? 'false' : 'true';
  checkout.setAttribute('aria-disabled', enabled ? 'false' : 'true');
}

function setMobileStickyVisibility(sticky, visible) {
  if (!(sticky instanceof HTMLElement)) return;

  const bar = sticky.querySelector('[ref="stickyBar"]');
  sticky.dataset.requiredSizeMobileVisible = visible ? 'true' : 'false';

  if (!(bar instanceof HTMLElement)) return;

  if (!MOBILE_MEDIA_QUERY.matches) {
    bar.removeAttribute('aria-hidden');
    bar.removeAttribute('inert');
    return;
  }

  bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
  bar.toggleAttribute('inert', !visible);
}

function isInViewport(element) {
  if (!(element instanceof Element)) return false;

  const rect = element.getBoundingClientRect();
  return (
    rect.bottom > 0 &&
    rect.top < window.innerHeight &&
    rect.right > 0 &&
    rect.left < window.innerWidth
  );
}

function syncMobileStickyVisibility(picker, state) {
  const { sticky } = getContext(picker);
  if (!(sticky instanceof HTMLElement)) return;

  if (!MOBILE_MEDIA_QUERY.matches) {
    setMobileStickyVisibility(sticky, true);
    return;
  }

  setMobileStickyVisibility(sticky, !state.mobileStickyMainActionVisible);
}

function setupMobileStickyVisibility(picker, state) {
  const { productForm, sticky } = getContext(picker);
  if (!(productForm instanceof HTMLElement) || !(sticky instanceof HTMLElement)) return;

  const mainButton = productForm.querySelector('[ref="addToCartButton"]');
  if (!(mainButton instanceof HTMLButtonElement)) return;

  const target = mainButton.closest('add-to-cart-component') || mainButton;

  if (state.mobileStickyTarget !== target) {
    state.mobileStickyObserver?.disconnect();
    state.mobileStickyTarget = target;
    state.mobileStickyMainActionVisible = isInViewport(target);

    if ('IntersectionObserver' in window) {
      state.mobileStickyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        state.mobileStickyMainActionVisible = entry.isIntersecting;
        syncMobileStickyVisibility(picker, state);
      });
      state.mobileStickyObserver.observe(target);
    } else {
      state.mobileStickyObserver = null;
    }
  }

  if (!state.mobileMediaHandler) {
    state.mobileMediaHandler = () => {
      if (MOBILE_MEDIA_QUERY.matches && state.mobileStickyTarget) {
        state.mobileStickyMainActionVisible = isInViewport(
          state.mobileStickyTarget
        );
      }
      syncMobileStickyVisibility(picker, state);
    };

    if (typeof MOBILE_MEDIA_QUERY.addEventListener === 'function') {
      MOBILE_MEDIA_QUERY.addEventListener('change', state.mobileMediaHandler);
    } else {
      MOBILE_MEDIA_QUERY.addListener?.(state.mobileMediaHandler);
    }
  }

  syncMobileStickyVisibility(picker, state);
}

function setStickyState(sticky, hasSelection, selectedSize, errorId) {
  if (!(sticky instanceof HTMLElement)) return;

  sticky.dataset.sizeSelected = hasSelection ? 'true' : 'false';

  const button = sticky.querySelector('[data-required-size-sticky-button]');
  const label = sticky.querySelector('[data-required-size-sticky-label]');
  const size = sticky.querySelector('[data-required-size-sticky-size]');

  if (size instanceof HTMLElement) {
    size.textContent = hasSelection && selectedSize
      ? `Size: ${selectedSize.label || selectedSize.value}`
      : '';
  }

  if (button instanceof HTMLButtonElement && label instanceof HTMLElement) {
    button.removeAttribute('on:click');
    let nextLabel = getButtonNativeText(button);
    if (!button.disabled) {
      nextLabel = hasSelection ? LABELS.addToCart : LABELS.selectSize;
    }

    if (nextLabel) {
      label.textContent = nextLabel;
      button.setAttribute('aria-label', nextLabel);
    }
    if (errorId) button.setAttribute('aria-describedby', errorId);
  }

  // This controller owns required-size sticky clicks; Horizon still owns the
  // real product-form submit and therefore the cart drawer update.
  sticky.removeAttribute('inert');
}

function updateSizeState(picker, hasSelection, selectedSize = null) {
  const { productForm, sticky, layout, section } = getContext(picker);
  const errorId = getSizeError(picker)?.id || '';
  const selected = selectedSize || getSelectedSize(getSizeContainer(picker));
  const stateValue = hasSelection ? 'true' : 'false';

  picker.dataset.sizeSelected = stateValue;
  if (productForm instanceof HTMLElement) productForm.dataset.sizeSelected = stateValue;
  if (layout instanceof HTMLElement) layout.dataset.sizeSelected = stateValue;
  if (section instanceof HTMLElement) section.dataset.sizeSelected = stateValue;

  setMainButtonState(productForm, hasSelection, errorId);
  setAcceleratedCheckoutState(productForm, hasSelection);
  setStickyState(sticky, hasSelection, selected, errorId);

  const state = pickerStates.get(picker);
  if (state) setupMobileStickyVisibility(picker, state);

  if (hasSelection) hideSizeError(picker);
}

function restartAnimation(element, className) {
  if (!(element instanceof HTMLElement)) return;

  element.classList.remove(className);
  void element.offsetWidth;
  element.classList.add(className);

  const clear = () => element.classList.remove(className);
  element.addEventListener('animationend', clear, { once: true });
  window.setTimeout(clear, 700);
}

function revealSizeError(picker, sourceButton) {
  if (getSelectedSize(getSizeContainer(picker))) {
    updateSizeState(picker, true);
    return;
  }

  showSizeError(picker);
  restartAnimation(sourceButton, 'required-size-button--shake');
  restartAnimation(getSizeContainer(picker), 'required-size-option--attention');
}

function requestSizeSelection(picker, sourceButton) {
  const sizeContainer = getSizeContainer(picker);
  if (!sizeContainer) return;

  const sticky = getContext(picker).sticky;
  const stickyHeight =
    sticky instanceof HTMLElement ? sticky.getBoundingClientRect().height : 0;
  const rect = sizeContainer.getBoundingClientRect();
  const isOutsideUsableViewport =
    rect.top < 0 || rect.bottom > window.innerHeight - stickyHeight - 12;

  const isStickyTrigger = Boolean(sourceButton?.closest('sticky-add-to-cart'));
  const shouldScroll =
    isOutsideUsableViewport && (MOBILE_MEDIA_QUERY.matches || isStickyTrigger);

  if (!shouldScroll) {
    revealSizeError(picker, sourceButton);
    return;
  }

  sizeContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

  let completed = false;
  const finish = () => {
    if (completed) return;
    completed = true;
    window.removeEventListener('scrollend', finish);
    revealSizeError(picker, sourceButton);
  };

  if ('onscrollend' in window) {
    window.addEventListener('scrollend', finish, { once: true });
  }
  window.setTimeout(finish, 700);
}

function isAcceleratedCheckoutClick(event, productForm) {
  if (!(productForm instanceof HTMLElement)) return false;

  const checkout = productForm.querySelector('.accelerated-checkout-block');
  if (!(checkout instanceof HTMLElement)) return false;

  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  if (path.includes(checkout)) return true;

  const target = event.target;
  return target instanceof Element && Boolean(target.closest('.accelerated-checkout-block'));
}

function bindActionControls(picker) {
  const { productForm, sticky } = getContext(picker);

  if (
    productForm instanceof HTMLElement &&
    productForm.dataset.requiredSizeValidationBound !== 'true'
  ) {
    productForm.dataset.requiredSizeValidationBound = 'true';

    productForm.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const dummyButton =
          target instanceof Element
            ? target.closest('[data-required-size-dummy-button]')
            : null;
        const button =
          target instanceof Element
            ? target.closest('[ref="addToCartButton"]')
            : null;
        const isDummyButton =
          dummyButton instanceof HTMLButtonElement && productForm.contains(dummyButton);
        const isMainButton =
          button instanceof HTMLButtonElement && productForm.contains(button);
        const isAcceleratedCheckout = isAcceleratedCheckoutClick(event, productForm);

        if (!isDummyButton && !isMainButton && !isAcceleratedCheckout) return;

        const selectedSize = getSelectedSize(getSizeContainer(picker));
        if (selectedSize) {
          updateSizeState(picker, true, selectedSize);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        const feedbackButton = isDummyButton
          ? dummyButton
          : isMainButton
            ? button
            : productForm.querySelector('[ref="addToCartButton"]');
        requestSizeSelection(
          picker,
          feedbackButton instanceof HTMLButtonElement ? feedbackButton : null
        );
      },
      true
    );

    productForm.addEventListener(
      'submit',
      (event) => {
        const selectedSize = getSelectedSize(getSizeContainer(picker));
        if (selectedSize) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const button = productForm.querySelector('[ref="addToCartButton"]');
        requestSizeSelection(
          picker,
          button instanceof HTMLButtonElement ? button : null
        );
      },
      true
    );
  }

  if (
    sticky instanceof HTMLElement &&
    sticky.dataset.requiredSizeValidationBound !== 'true'
  ) {
    sticky.dataset.requiredSizeValidationBound = 'true';

    sticky.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('[data-required-size-sticky-button]');
        if (!(button instanceof HTMLButtonElement)) return;

        const selectedSize = getSelectedSize(getSizeContainer(picker));
        if (selectedSize) {
          event.preventDefault();
          event.stopImmediatePropagation();
          updateSizeState(picker, true, selectedSize);

          const currentProductForm = getContext(picker).productForm;
          const mainButton = currentProductForm?.querySelector(
            '[ref="addToCartButton"]'
          );
          if (!(mainButton instanceof HTMLButtonElement) || mainButton.disabled) return;

          button.dataset.added = 'true';
          mainButton.dataset.puppet = 'true';
          mainButton.click();
          window.setTimeout(() => {
            button.removeAttribute('data-added');
            mainButton.dataset.puppet = 'false';
          }, 1800);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        requestSizeSelection(picker, button);
      },
      true
    );
  }
}

function restoreStateAfterVariantRender(picker, state) {
  if (!picker.isConnected) {
    initializeRequiredSizePickers(document);
    return;
  }

  const sizeContainer = getSizeContainer(picker);
  if (!sizeContainer) return;

  bindActionControls(picker);

  if (!state.rememberedSize) {
    clearSizeSelection(sizeContainer);
    updateSizeState(picker, false);
    return;
  }

  const rememberedMatch = findRememberedSize(
    sizeContainer,
    state.rememberedSize
  );

  if (!rememberedMatch) {
    clearSizeSelection(sizeContainer);
    updateSizeState(picker, false);
    return;
  }

  if (!rememberedSizeIsSelected(rememberedMatch)) {
    markSizeSelected(sizeContainer, rememberedMatch);
    const selectedSize = getSelectedSize(sizeContainer);
    updateSizeState(picker, Boolean(selectedSize), selectedSize);

    rememberedMatch.control.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  const selectedSize = getSelectedSize(sizeContainer);
  updateSizeState(picker, Boolean(selectedSize), selectedSize);
}

function initializePicker(picker) {
  if (
    !(picker instanceof HTMLElement) ||
    picker.dataset.sizeValidationReady === 'true'
  ) {
    return;
  }

  const initialSizeContainer = getSizeContainer(picker);
  if (!initialSizeContainer) return;

  picker.dataset.sizeValidationReady = 'true';

  const state = {
    rememberedSize: null,
    requestGeneration: 0,
    mobileStickyObserver: null,
    mobileStickyTarget: null,
    mobileStickyMainActionVisible: false,
    mobileMediaHandler: null,
  };
  pickerStates.set(picker, state);

  // Horizon is upgraded before this runs, so its internal radio references stay intact.
  clearSizeSelection(initialSizeContainer);
  updateSizeState(picker, false);
  bindActionControls(picker);

  picker.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('[data-size-option]')) {
        return;
      }

      const selectedSize = getSelectedSize(getSizeContainer(picker));
      state.rememberedSize = getSizeMemory(selectedSize);
      updateSizeState(picker, Boolean(selectedSize), selectedSize);
    },
    true
  );

  picker.addEventListener(PRODUCT_SELECT_EVENT, (event) => {
    const generation = ++state.requestGeneration;
    const promise = event.promise;

    if (!state.rememberedSize) {
      const { productForm } = getContext(picker);
      productForm?.setAttribute('inert', '');
    }

    if (!(promise instanceof Promise)) {
      nextFrame(() => restoreStateAfterVariantRender(picker, state));
      return;
    }

    promise
      .then(() => {
        if (generation !== state.requestGeneration) return;
        nextFrame(() => restoreStateAfterVariantRender(picker, state));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[required-size-selection] Variant update failed.', error);
          nextFrame(() => restoreStateAfterVariantRender(picker, state));
        }
      });
  });

  // Backward compatibility for older Horizon builds that emitted this event.
  picker.addEventListener('variant:update', () => {
    nextFrame(() => restoreStateAfterVariantRender(picker, state));
  });
}

function initializeRequiredSizePickers(root = document) {
  if (!(root instanceof Document || root instanceof Element)) return;
  root.querySelectorAll(REQUIRED_PICKER_SELECTOR).forEach(initializePicker);

  if (root instanceof Element && root.matches(REQUIRED_PICKER_SELECTOR)) {
    initializePicker(root);
  }
}

function bootRequiredSizeSelection(root = document) {
  const initialize = () => requestAnimationFrame(() => initializeRequiredSizePickers(root));

  if (customElements.get('variant-picker')) {
    initialize();
  } else {
    customElements.whenDefined('variant-picker').then(initialize);
  }
}

bootRequiredSizeSelection();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    bootRequiredSizeSelection(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;

  event.target.querySelectorAll(REQUIRED_PICKER_SELECTOR).forEach((picker) => {
    if (!(picker instanceof HTMLElement)) return;

    const state = pickerStates.get(picker);
    if (!state) return;

    state.mobileStickyObserver?.disconnect();
    if (state.mobileMediaHandler) {
      if (typeof MOBILE_MEDIA_QUERY.removeEventListener === 'function') {
        MOBILE_MEDIA_QUERY.removeEventListener('change', state.mobileMediaHandler);
      } else {
        MOBILE_MEDIA_QUERY.removeListener?.(state.mobileMediaHandler);
      }
    }
  });
});
